// src/routes/index.jsx

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Layouts
import { AppLayout } from '../components/layout/AppLayout';

// Pages
import { LoginPage } from '../pages/auth/LoginPage';
import { SignupPage } from '../pages/auth/SignupPage';
import { InstallAppPage } from '../pages/auth/InstallAppPage';
import { OnboardingPage } from '../pages/auth/OnboardingPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { ProgressPage } from '../pages/profile/ProgressPage';
import { TestOutReportPage } from '../pages/profile/TestOutReportPage';
import { ProgramPage } from '../pages/program/ProgramPage';
import { CalendarPage } from '../pages/calendar/CalendarPage';
import { AboutPage } from '../pages/AboutPage';
import { FaqPage } from '../pages/FaqPage';
import { PatientsPage, UsersPage, PatientDetailPage, UserDetailPage } from '../pages/admin';

// Protected Route Wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }

  const aclOnboardingCompleted = user?.acl_care?.acl_onboarding_completed;

  // If onboarding NOT completed and NOT on onboarding page, redirect to onboarding
  if (!aclOnboardingCompleted && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // If onboarding IS completed and ON onboarding page, redirect to program
  if (aclOnboardingCompleted && location.pathname === '/onboarding') {
    return <Navigate to="/program" replace />;
  }

  return children;
}

// Public Route - redirects authenticated users away from public pages
function PublicRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    const aclOnboardingCompleted = user?.acl_care?.acl_onboarding_completed;

    if (!aclOnboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    const returnTo = location.state?.returnTo || '/program';
    return <Navigate to={returnTo} replace />;
  }

  return children;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          }
        />

        <Route
          path="/install-app"
          element={
            <PublicRoute>
              <InstallAppPage />
            </PublicRoute>
          }
        />

        {/* Onboarding */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/program" replace />} />

          {/* Profile Routes */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/progress" element={<ProgressPage />} />
          <Route path="/profile/test-out-report" element={<TestOutReportPage />} />

          {/* Program Routes */}
          <Route path="/program/*" element={<ProgramPage />} />

          {/* Calendar */}
          <Route path="/calendar" element={<CalendarPage />} />

          {/* Support Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />

          {/* Admin Routes */}
          <Route path="/clients" element={<PatientsPage />} />
          <Route path="/clients/:patientId" element={<PatientDetailPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:userId" element={<UserDetailPage />} />

        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}